// Spec 48 ai-ux — citation_chip.
//
// "Inline pill citing a source record. Shows record kind + short
// identifier. Hoverable preview with 300ms delay; click opens full
// source; keyboard-focusable with Enter to open, space to preview."
// (spec 48 line 681)
//
// a11y per spec 48:
//   - Chip is a button with aria-label='Citation: {kind} {short_id} —
//     Enter to open'
//   - Hover preview is keyboard-reachable via space key, not just mouse
//   - Disabled chips use aria-disabled='true' AND retain focus behavior
//     (so a screen reader can land on them and announce "out of scope")

import {
  forwardRef,
  useCallback,
  type ButtonHTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";

import { cx } from "../lib/cx.ts";

export interface CitationChipProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "disabled" | "onClick"> {
  /** Record kind: "employee" | "report" | "doc" | "policy" | ... */
  readonly recordKind: string;
  /** Short id or truncated name (≤16 chars in display, no enforcement here). */
  readonly shortId: string;
  /** Decorative kind icon (renders aria-hidden). */
  readonly icon?: ReactNode;
  /** Click → open full source record. */
  readonly onOpen: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Space-key → open hover-preview equivalent (caller renders the popover). */
  readonly onPreview?: () => void;
  /** When true, chip is disabled with a tooltip explaining out-of-scope. */
  readonly isOutOfScope?: boolean;
  /** Tooltip text explaining the disabled state ("Out of scope — ask {who}"). */
  readonly outOfScopeTooltip?: string;
  /** Override aria-label suffix instructions ("Enter to open"). */
  readonly enterToOpenLabel?: string;
}

export const CitationChip = forwardRef<HTMLButtonElement, CitationChipProps>(
  (
    {
      recordKind,
      shortId,
      icon,
      onOpen,
      onPreview,
      isOutOfScope = false,
      outOfScopeTooltip,
      enterToOpenLabel = "Enter to open",
      className,
      ...rest
    },
    ref,
  ) => {
    // aria-label per spec a11y: "Citation: {kind} {short_id} — Enter to open"
    const ariaLabel = isOutOfScope
      ? `Citation: ${recordKind} ${shortId} — ${outOfScopeTooltip ?? "Out of scope"}`
      : `Citation: ${recordKind} ${shortId} — ${enterToOpenLabel}`;

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLButtonElement>) => {
        // Space → preview (hover keyboard equivalent per spec).
        if (event.key === " " && onPreview !== undefined && !isOutOfScope) {
          event.preventDefault();
          onPreview();
        }
        // Enter falls through to native button click — already wired.
      },
      [onPreview, isOutOfScope],
    );

    return (
      <button
        ref={ref}
        type="button"
        data-component="citation-chip"
        data-kind={recordKind}
        // aria-disabled (not native disabled): retains focus behavior so
        // screen readers can land on the chip and announce out-of-scope
        // tooltip per spec 48 a11y rule line 713.
        aria-disabled={isOutOfScope || undefined}
        aria-label={ariaLabel}
        title={isOutOfScope ? outOfScopeTooltip : undefined}
        className={cx(
          "nai-citation-chip",
          isOutOfScope && "nai-citation-chip--out-of-scope",
          className,
        )}
        onClick={(event) => {
          if (isOutOfScope) return;
          onOpen(event);
        }}
        onKeyDown={handleKeyDown}
        {...rest}
      >
        {icon !== undefined && (
          <span aria-hidden="true" data-component="citation-chip-icon" className="nai-citation-chip-icon">
            {icon}
          </span>
        )}
        <span data-component="citation-chip-kind" className="nai-citation-chip-kind">
          {recordKind}
        </span>
        <span aria-hidden="true" className="nai-citation-chip-separator">
          {": "}
        </span>
        <span data-component="citation-chip-short-id" className="nai-citation-chip-short-id">
          {shortId}
        </span>
      </button>
    );
  },
);
CitationChip.displayName = "CitationChip";
